import { formatCurrency } from './formatCurrency';

// What a customer or property costs per visit, and what to show when nobody has
// said yet.
//
// WHY THIS EXISTS
// Twelve call sites across Customers.js and StormMode.js used to read
// `parseFloat(row.price_snow || 50)`. That fallback invented a price:
//
//   - A customer whose price is not set rendered $50.00, while the server's
//     price engine (services/pricing.js) reads a NULL price_snow as
//     `base:missing` and bills 0. So the driver's screen said $50.00 for a stop
//     the server billed at $0.00 - both wrong, in opposite directions, at the
//     same moment.
//   - A price genuinely set to 0 also rendered $50.00, because `0 || 50` is 50.
//     A free driveway is a real thing an owner can set and could not display.
//
// Since the intake form stopped inventing prices, a NULL price_snow is now a
// normal state a new customer sits in until the owner confirms the driveway
// size. Rendering it as $50.00 would move the phantom price out of the database
// and into the UI, where it is harder to see and impossible to query.
//
// NULLISH, NOT FALSY. The check is `== null` on purpose, matching how
// services/pricing.js decides a price is missing, so the client and the server
// agree about the same row. 0 is a price. NULL is not.

// True when this row has a snow price at all.
export const hasStopPrice = (row) => row != null && row.price_snow != null && row.price_snow !== '';

// The per-visit total: the snow price plus salt when salt applies. Returns null
// when there is no price, so a caller doing arithmetic has to decide what that
// means rather than silently treating it as zero.
export const stopTotal = (row, includeSalt) => {
  if (!hasStopPrice(row)) return null;
  const base = parseFloat(row.price_snow) || 0;
  const salt = includeSalt ? (parseFloat(row.salt_price) || 0) : 0;
  return base + salt;
};

// PRICE LOCK (migration 070). A COMPLETED stop's price is not computed in the
// browser at all. The server returns it with the route: charged_total and
// charged_subtotal (checked against the stored lock on every read), with
// locked_total and locked_subtotal on the stop row itself. The browser only
// displays them, so the screen the owner edits from cannot disagree with the
// invoice, the portal or the statement, and a later change to the customer's
// price or discount cannot move a stop that has been worked.
export const isUnpricedLockedStop = (stop) =>
  stop != null && stop.status === 'completed' &&
  (stop.price_unpriced === true || stop.price_lock_source === 'unpriced');

// DEPTH PRICING. A stop completed on a depth route before the storm depth was
// entered has no figure yet: the server sends price_pending_depth (route
// detail) and price_lock_source 'pending_depth'. It is shown as awaiting
// depth, never as unpriced and never as $0.
export const isPendingDepthStop = (stop) =>
  stop != null && stop.status === 'completed' &&
  (stop.price_pending_depth === true || stop.price_lock_source === 'pending_depth');

export const AWAITING_DEPTH_LABEL = 'Awaiting depth';

// The depth band a completed stop was priced at ('6.1-9 in'), or null. Route
// detail sends depth_band_label; raw stop rows carry the lock snapshot.
export const stopDepthBand = (stop) =>
  (stop && (stop.depth_band_label || stop.locked_depth_band_label)) || null;

// 'Snow removal, 6.1-9 in', or plain 'Snow removal' on a flat stop.
export const snowServiceLabel = (band) => (band ? `Snow removal, ${band}` : 'Snow removal');

// The on-call premium percentage locked on a stop, or null when there is none.
export const stopPremiumPercent = (stop) => {
  const p = stop == null ? NaN : parseFloat(stop.locked_on_call_premium_percent);
  return Number.isFinite(p) && p > 0 ? p : null;
};

// 'On-call premium (20%)'. Trailing '.00' dropped, as the invoice line does.
export const premiumLineLabel = (percent) =>
  `On-call premium (${Number(parseFloat(percent).toFixed(2))}%)`;

// The stored total for a completed stop, or null when it has none (a stop not
// completed, or one completed with no price set).
export const lockedStopTotal = (stop) => {
  if (stop == null || stop.status !== 'completed') return null;
  const v = stop.charged_total != null ? stop.charged_total : stop.locked_total;
  return v == null ? null : parseFloat(v);
};

// The stored subtotal (before discount) for a completed stop, or null.
export const lockedStopSubtotal = (stop) => {
  if (stop == null || stop.status !== 'completed') return null;
  const v = stop.charged_subtotal != null ? stop.charged_subtotal : stop.locked_subtotal;
  return v == null ? null : parseFloat(v);
};

// What to render. One string, so every list, card and tooltip says the same
// thing about an unpriced row.
export const NO_PRICE_LABEL = 'No price set';

export const formatStopTotal = (row, includeSalt) => {
  const total = stopTotal(row, includeSalt);
  return total === null ? NO_PRICE_LABEL : formatCurrency(total);
};
